import type { PermissionDef, PermissionGroup, RoleItem, UserDetail, UserListItem } from "@nmt/shared";
import { NotifyGrid } from "../../components/notify-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { errorMessage, get, patch, post } from "../../api";
import { Alert, Button, Checkbox, Dialog, Field, Loading, Panel, PanelHeader, SelectField, cx, formatDateTime } from "../../components/ui";
import type { SettingsResponse } from "./useSettings";
import { useSession } from "../../session";
import { statusBadge } from "./Users";

type OverrideValue = "role" | "grant" | "revoke";

export function UserDetailPage() {
  const { id = "" } = useParams();
  const { me, can } = useSession();
  const qc = useQueryClient();
  const person = useQuery({ queryKey: ["users", id], queryFn: () => get<UserDetail>(`/api/users/${id}`) });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles") });
  // The RingCentral extensions the CRM watches (Settings → Texts and calls); only for people who manage settings.
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => get<SettingsResponse>("/api/settings"), enabled: can("settings.manage") });
  const watched = settings.data?.settings["comms.extensions"];
  const extensions = watched?.length ? watched : null;
  const catalog = useQuery({
    queryKey: ["permissions"],
    queryFn: () => get<{ groups: PermissionGroup[]; permissions: PermissionDef[] }>("/api/permissions"),
  });

  const [profile, setProfile] = useState({ firstName: "", lastName: "", phone: "", color: "#D71920", rcExtensionId: "", commission: "", managerId: "" });
  const people = useQuery({ queryKey: ["users"], queryFn: () => get<UserListItem[]>("/api/users") });
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Record<string, OverrideValue>>({});
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const p = person.data;
    if (!p) return;
    setProfile({ firstName: p.firstName, lastName: p.lastName, phone: p.phone ?? "", color: p.color, rcExtensionId: p.rcExtensionId ?? "", commission: p.commissionPercent === null ? "" : String(p.commissionPercent), managerId: p.managerId ?? "" });
    setRoleIds(p.roleIds);
    setOverrides(Object.fromEntries(p.overrides.map((o) => [o.permission, o.effect])));
  }, [person.data]);

  const onSaved = async (d: UserDetail, msg: string) => {
    qc.setQueryData(["users", id], d);
    await qc.invalidateQueries({ queryKey: ["users"] });
    await qc.invalidateQueries({ queryKey: ["users-directory"] });
    await qc.invalidateQueries({ queryKey: ["roles"] });
    if (d.id === me?.user.id) await qc.invalidateQueries({ queryKey: ["me"] });
    setNotice(msg);
  };

  const save = useMutation({
    mutationFn: () =>
      patch<UserDetail>(`/api/users/${id}`, {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone || null,
        color: profile.color,
        ...(extensions ? { rcExtensionId: profile.rcExtensionId || null } : {}),
        commissionPercent: profile.commission.trim() === "" ? null : Number(profile.commission),
        managerId: profile.managerId || null,
        roleIds,
        ...(can("roles.manage")
          ? {
              overrides: Object.entries(overrides)
                .filter(([, v]) => v !== "role")
                .map(([permission, effect]) => ({ permission, effect })),
            }
          : {}),
      }),
    onSuccess: (d) => onSaved(d, "Changes saved."),
  });
  const deactivate = useMutation({
    mutationFn: () => post<UserDetail>(`/api/users/${id}/deactivate`),
    onSuccess: (d) => {
      setConfirmDeactivate(false);
      return onSaved(d, "Deactivated. They've been signed out everywhere.");
    },
  });
  const reactivate = useMutation({
    mutationFn: () => post<UserDetail>(`/api/users/${id}/reactivate`),
    onSuccess: (d) => onSaved(d, "Reactivated. They can sign in again."),
  });

  // Live preview of what this person will be able to do.
  const preview = useMemo(() => {
    if (!roles.data) return new Set<string>();
    const set = new Set<string>();
    for (const r of roles.data) if (roleIds.includes(r.id)) r.permissions.forEach((p) => set.add(p));
    for (const [p, v] of Object.entries(overrides)) {
      if (v === "grant") set.add(p);
      if (v === "revoke") set.delete(p);
    }
    return set;
  }, [roles.data, roleIds, overrides]);

  if (person.isLoading || roles.isLoading) return <Loading />;
  if (person.error) return <Alert>{errorMessage(person.error)}</Alert>;
  const p = person.data!;
  const isSelf = p.id === me?.user.id;
  const error = save.error ?? deactivate.error ?? reactivate.error;

  const permissionsFromRoles = new Set<string>();
  for (const r of roles.data ?? []) if (roleIds.includes(r.id)) r.permissions.forEach((x) => permissionsFromRoles.add(x));

  return (
    <div>
      <Link to="/settings/users" className="text-sm font-medium text-muted hover:text-ink">
        Users
      </Link>
      <header className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="page-title">
            {p.firstName} {p.lastName}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-ink-soft">
            {statusBadge(p.status)}
            <span>{p.email}</span>
            <span className="text-muted">Last sign-in {p.lastSignInAt ? formatDateTime(p.lastSignInAt, me?.company.timezone) : "never"}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {p.status === "deactivated" ? (
            <Button onClick={() => reactivate.mutate()} busy={reactivate.isPending}>
              Reactivate
            </Button>
          ) : (
            !isSelf && (
              <Button variant="danger" onClick={() => setConfirmDeactivate(true)}>
                Deactivate
              </Button>
            )
          )}
        </div>
      </header>

      <div className="mb-4 flex flex-col gap-2">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {error && <Alert>{errorMessage(error)}</Alert>}
      </div>

      <form
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]"
        onSubmit={(e) => {
          e.preventDefault();
          setNotice(null);
          save.mutate();
        }}
      >
        <div className="flex flex-col gap-6">
          <Panel>
            <PanelHeader title="Profile" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
              <Field label="First name" required value={profile.firstName} onChange={(e) => setProfile({ ...profile, firstName: e.target.value })} />
              <Field label="Last name" required value={profile.lastName} onChange={(e) => setProfile({ ...profile, lastName: e.target.value })} />
              <Field label="Mobile" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              <Field label="Email" value={p.email} disabled hint="Their Google sign-in; it can't be changed here." />
              {extensions && (
                <SelectField
                  label="RingCentral extension"
                  value={profile.rcExtensionId}
                  onChange={(e) => setProfile({ ...profile, rcExtensionId: e.target.value })}
                  hint="Calls ringing it pop up for them, and they see its texts, calls, and voicemails. Extensions to pick from are set in Settings → Texts and calls."
                >
                  <option value="">None</option>
                  {extensions.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.number} · {x.name}
                    </option>
                  ))}
                  {profile.rcExtensionId && !extensions.some((x) => x.id === profile.rcExtensionId) && <option value={profile.rcExtensionId}>An extension no longer watched</option>}
                </SelectField>
              )}
              <Field
                label="Default commission, % of net profit"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={profile.commission}
                onChange={(e) => setProfile({ ...profile, commission: e.target.value })}
                hint="Of a job's net profit after the overhead (Jason: 40). Shown on the job P&L for jobs they're the sales rep on. Blank: none."
              />
              <SelectField label="Reports to" value={profile.managerId} onChange={(e) => setProfile({ ...profile, managerId: e.target.value })} hint="Their manager can get notifications on their jobs (with “Notifications for my team's jobs”).">
                <option value="">No one</option>
                {people.data
                  ?.filter((u) => u.id !== id && u.status !== "deactivated")
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
              </SelectField>
              <div>
                <label htmlFor="user-color" className="mb-1 block text-sm font-semibold text-ink-soft">
                  Calendar color
                </label>
                <input id="user-color" type="color" value={profile.color} onChange={(e) => setProfile({ ...profile, color: e.target.value.toUpperCase() })} className="h-10 w-14 cursor-pointer rounded border border-line" />
              </div>
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Roles" description="A person can have more than one role. Their permissions combine." />
            <div className="grid gap-1 px-3 py-3 sm:grid-cols-2">
              {roles.data?.map((r) => (
                <Checkbox
                  key={r.id}
                  label={r.name}
                  description={r.description}
                  checked={roleIds.includes(r.id)}
                  onChange={(on) => setRoleIds((ids) => (on ? [...ids, r.id] : ids.filter((x) => x !== r.id)))}
                />
              ))}
            </div>
          </Panel>

          {can("roles.manage") && catalog.data && (
            <Panel>
              <PanelHeader
                title="Individual permissions"
                description="Use sparingly. These override this person's roles, one permission at a time."
              />
              <ul>
                {catalog.data.permissions.map((perm) => (
                  <li key={perm.key} className="flex flex-col gap-2 border-b border-mist px-5 py-3 last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{perm.label}</div>
                      <div className="text-sm text-muted">{perm.available ? perm.description : "Used by a feature coming in a later phase."}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="w-36 text-sm">
                        <span className="text-muted">From roles: </span>
                        {permissionsFromRoles.has(perm.key) ? "Allowed" : <span className="text-muted">Not allowed</span>}
                      </span>
                      <OverrideToggle value={overrides[perm.key] ?? "role"} onChange={(v) => setOverrides((o) => ({ ...o, [perm.key]: v }))} label={perm.label} />
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          <div className="flex justify-end">
            <Button variant="primary" type="submit" busy={save.isPending}>
              Save changes
            </Button>
          </div>

          <div>
            <h2 className="text-lg font-semibold">Notifications</h2>
            <p className="mb-3 text-sm text-muted">Saved as you change them. They see the same in My profile → Notifications (and can change them unless locked).</p>
            <NotifyGrid url={`/api/users/${id}/notifications`} admin name={`${p.firstName}'s`} />
          </div>
        </div>

        <aside>
          <Panel className="sticky top-6">
            <PanelHeader title="What they can do" description="Updates as you change roles. Saved when you save." />
            <ul className="flex flex-col gap-2 px-5 py-4 text-sm">
              {catalog.data?.permissions.map((perm) => {
                const on = preview.has(perm.key);
                return (
                  <li key={perm.key} className={cx("flex items-start gap-2", !on && "text-muted")}>
                    <span className={cx("mt-1.5 size-2 shrink-0 rounded-full", on ? "bg-ok" : "border border-line")} aria-hidden />
                    <span>
                      {perm.label}
                      <span className="sr-only">{on ? " (allowed)" : " (not allowed)"}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </Panel>
        </aside>
      </form>

      {confirmDeactivate && (
        <Dialog
          title={`Deactivate ${p.firstName}?`}
          onClose={() => setConfirmDeactivate(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setConfirmDeactivate(false)}>
                Cancel
              </Button>
              <Button variant="danger" busy={deactivate.isPending} onClick={() => deactivate.mutate()}>
                Deactivate
              </Button>
            </>
          }
        >
          <p className="text-ink-soft">
            {p.firstName} will be signed out everywhere and won't be able to sign in. Their history stays in the activity log. You can reactivate them later.
          </p>
        </Dialog>
      )}
    </div>
  );
}

function OverrideToggle({ value, onChange, label }: { value: OverrideValue; onChange: (v: OverrideValue) => void; label: string }) {
  const options: { v: OverrideValue; text: string }[] = [
    { v: "role", text: "Use roles" },
    { v: "grant", text: "Allow" },
    { v: "revoke", text: "Deny" },
  ];
  return (
    <div role="radiogroup" aria-label={`Override for ${label}`} className="inline-flex rounded-[var(--radius-control)] border border-line bg-mist p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          onClick={() => onChange(o.v)}
          className={cx(
            "rounded-[6px] px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
            value === o.v
              ? o.v === "grant"
                ? "bg-ok text-white"
                : o.v === "revoke"
                  ? "bg-danger text-white"
                  : "bg-white text-ink shadow-sm"
              : "text-muted hover:text-ink",
          )}
        >
          {o.text}
        </button>
      ))}
    </div>
  );
}
